/**
 * Cart Free Gift Manager
 * Automatically adds/removes a free gift product when cart reaches/falls below threshold
 */

class CartFreeGiftManager {
  constructor() {
    this.progressBar = document.querySelector('.progress-bar');

    // Get settings from data attributes (progress bar) or global config
    if (this.progressBar) {
      this.enabled = this.progressBar.dataset.autoGiftEnabled === 'true';
      this.variantId = this.progressBar.getAttribute('data-auto-gift-variant-id');
      this.threshold = parseFloat(this.progressBar.getAttribute('data-checkpoint-3-threshold'));
      this.checkpoint2Collection = this.progressBar.getAttribute('data-checkpoint-2-collection');
      this.checkpoint2DiscountCode = this.progressBar.getAttribute('data-checkpoint-2-discount-code');
      this.checkpoint2RemoveDiscount = this.progressBar.getAttribute('data-checkpoint-2-remove-discount') === 'true';
      this.checkpoint3Collection = this.progressBar.getAttribute('data-checkpoint-3-collection');
      this.giftPropertyLabel = this.progressBar.dataset.giftPropertyLabel || 'Free Gift';
      this.lang = this.progressBar.dataset.lang || 'nl';
    } else if (window.cartFreeGiftConfig) {
      // Fallback to global config for pages without progress bar
      const config = window.cartFreeGiftConfig;
      this.enabled = config.autoGiftEnabled;
      this.variantId = config.autoGiftVariantId;
      this.threshold = parseFloat(config.checkpoint3Threshold);
      this.checkpoint2Collection = config.checkpoint2Collection;
      this.checkpoint2DiscountCode = config.checkpoint2DiscountCode;
      this.checkpoint2RemoveDiscount = config.checkpoint2RemoveDiscount;
      this.checkpoint3Collection = config.checkpoint3Collection;
      this.giftPropertyLabel = config.giftPropertyLabel || 'Free Gift';
      this.lang = config.lang || 'nl';
    } else {
      return;
    }

    this.discountCodeApplied = false;
    this.hadCheckpoint2Item = false;

    // For discount code functionality, we don't need the full gift management to be enabled
    const hasGiftConfig = this.enabled && this.variantId && this.threshold && !isNaN(this.threshold);
    const hasDiscountConfig = this.checkpoint2DiscountCode;

    if (!hasGiftConfig && !hasDiscountConfig) {
      return;
    }

    this.isProcessing = false;
    this.hasAutoAddedGift = false;

    this.init();
  }

  init() {
    // Listen for cart updates
    document.addEventListener('cart:update', (e) => this.handleCartUpdate(e));
    document.addEventListener('cart:updated', (e) => this.handleCartUpdate(e));

    // Listen for manual reclaim button clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('.progress-bar__reclaim-gift-btn')) {
        e.preventDefault();
        this.handleReclaimClick(e.target.closest('.progress-bar__reclaim-gift-btn'));
      }
    });

    // Check initial cart state (no section refresh needed on page load)
    this.checkInitialCartState();
  }

  async checkInitialCartState() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      const cart = await this.fetchCart();
      const cartTotal = this.calculateCartTotal(cart);
      const hasGiftInCart = this.hasGiftProduct(cart);

      // Check for checkpoint 2 item and apply discount code if needed
      if (this.checkpoint2DiscountCode && !this.discountCodeApplied) {
        const hasCheckpoint2 = this.hasCheckpoint2Item(cart);

        if (hasCheckpoint2) {
          await this.applyDiscountCode(this.checkpoint2DiscountCode);
          this.hadCheckpoint2Item = true;
        }
      }

      // Check for checkpoint 3 auto-gift (only if enabled)
      if (this.enabled && this.variantId && cartTotal >= this.threshold && !hasGiftInCart) {
        await this.addGiftToCart();
        this.hasAutoAddedGift = true;
      }
    } catch (error) {
      console.error('[Free Gift] Error:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  async handleCartUpdate(event) {
    if (this.isProcessing) return;
    await this.checkCartAndManageGift();
  }

  async checkCartAndManageGift() {
    if (this.isProcessing) return;

    this.isProcessing = true;

    try {
      const cart = await this.fetchCart();
      const cartTotal = this.calculateCartTotal(cart);
      const hasGiftInCart = this.hasGiftProduct(cart);

      // First refresh the progress bar to get updated Liquid-calculated values
      await this.refreshProgressBarSection();

      // Re-get the progress bar reference after refresh
      this.progressBar = document.querySelector('.progress-bar');

      // Check for checkpoint 2 item (gratis sieraad) and manage discount code
      if (this.checkpoint2DiscountCode) {
        const hasCheckpoint2 = this.hasCheckpoint2Item(cart);

        if (hasCheckpoint2 && !this.discountCodeApplied) {
          await this.applyDiscountCode(this.checkpoint2DiscountCode);
          this.hadCheckpoint2Item = true;
        } else if (!hasCheckpoint2 && this.hadCheckpoint2Item && this.checkpoint2RemoveDiscount) {
          await this.removeDiscountCode();
          this.hadCheckpoint2Item = false;
          this.discountCodeApplied = false;
        }
      }

      // Decision logic for checkpoint 3 auto-gift (only if enabled)
      if (this.enabled && this.variantId) {
        if (cartTotal >= this.threshold && !hasGiftInCart) {
          await this.addGiftToCart();
          this.hasAutoAddedGift = true;
        } else if (cartTotal < this.threshold && hasGiftInCart) {
          await this.removeGiftFromCart(cart);
          this.hasAutoAddedGift = false;
        }
      }
    } catch (error) {
      console.error('[Free Gift] Error:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  async handleReclaimClick(button) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      await this.addGiftToCart();
      this.hasAutoAddedGift = true;
    } catch (error) {
      console.error('[Free Gift] Error:', error);
      this.showError(error.message);
    } finally {
      this.isProcessing = false;
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) throw new Error('Failed to fetch cart');
    return await response.json();
  }

  calculateCartTotal(cart) {
    let totalCents = 0;

    cart.items.forEach(item => {
      let isFreeItem = false;

      if (this.checkpoint2Collection && ((item.original_line_price > 0 && item.final_price === 0) || (item.properties && item.properties._free_gift === 'true'))) {
        isFreeItem = true;
      }

      if (this.checkpoint3Collection && ((item.original_line_price > 0 && item.final_price === 0) || (item.properties && item.properties._free_gift === 'true'))) {
        isFreeItem = true;
      }

      if (String(item.variant_id) === String(this.variantId) ||
          (item.properties && item.properties._free_gift === 'true')) {
        isFreeItem = true;
      }

      if (!isFreeItem) {
        totalCents += item.final_line_price;
      }
    });

    return totalCents / 100;
  }

  hasGiftProduct(cart) {
    return cart.items.some(item => {
      return String(item.variant_id) === String(this.variantId) ||
             (item.properties && item.properties._free_gift === 'true');
    });
  }

  hasCheckpoint2Item(cart) {
    const progressBar = document.querySelector('.progress-bar');
    if (progressBar) {
      return progressBar.getAttribute('data-checkpoint-2-item-added') === 'true';
    }

    return cart.items.some(item => {
      const isFree = (item.original_line_price > 0 && item.final_price === 0) ||
                     (item.properties && item.properties._free_gift === 'true');
      return isFree;
    });
  }

  async applyDiscountCode(code) {
    if (!code || this.discountCodeApplied) return;

    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        credentials: 'same-origin'
      });
      this.discountCodeApplied = true;
    } catch (error) {
      console.error('[Free Gift] Error applying discount code:', error);
    }
  }

  async removeDiscountCode() {
    try {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('discount', '');
      window.location.href = '/discount/?redirect=' + encodeURIComponent(currentUrl.pathname);
    } catch (error) {
      console.error('[Free Gift] Error removing discount code:', error);
    }
  }

  async addGiftToCart() {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: this.variantId,
          quantity: 1,
          properties: {
            '_free_gift': 'true',
            [this.giftPropertyLabel]: '✓'
          }
        }]
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add gift to cart');
    }

    await this.refreshCart();
  }

  async removeGiftFromCart(cart) {
    const giftItems = cart.items.filter(item => {
      return String(item.variant_id) === String(this.variantId) ||
             (item.properties && item.properties._free_gift === 'true');
    });

    if (giftItems.length === 0) return;

    for (const item of giftItems) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: item.key,
          quantity: 0
        })
      });
    }

    await this.refreshCart();
  }

  async refreshCart() {
    const cart = await this.fetchCart();

    document.dispatchEvent(new CustomEvent('cart:update', {
      detail: { cart }
    }));

    const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count-bubble');
    cartCountElements.forEach(el => {
      el.textContent = cart.item_count;
    });

    this.refreshProgressBarSection();
  }

  async refreshProgressBarSection() {
    try {
      const cartComponent = document.querySelector('cart-items-component[data-section-id]');
      const sectionId = cartComponent ? cartComponent.dataset.sectionId : 'main-cart';

      const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newProgressBar = doc.querySelector('.progress-bar');

        if (newProgressBar && this.progressBar) {
          this.progressBar.outerHTML = newProgressBar.outerHTML;
          this.progressBar = document.querySelector('.progress-bar');
        }
      }
    } catch (error) {
      // Not critical, UI will update on page reload
    }
  }

  showError(message) {
    const errorMessages = {
      'nl': 'Kon gratis cadeau niet toevoegen. Probeer het opnieuw.',
      'de': 'Gratis Geschenk konnte nicht hinzugefügt werden. Bitte versuchen Sie es erneut.',
      'en': 'Could not add free gift. Please try again.'
    };

    const errorText = errorMessages[this.lang] || errorMessages['en'];

    if (this.progressBar) {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'progress-bar__error';
      errorDiv.style.color = 'red';
      errorDiv.style.padding = '10px';
      errorDiv.style.textAlign = 'center';
      errorDiv.textContent = errorText;

      this.progressBar.appendChild(errorDiv);
      setTimeout(() => errorDiv.remove(), 5000);
    } else {
      alert(errorText);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartFreeGiftManager();
  });
} else {
  new CartFreeGiftManager();
}
